import ReactDOM from "react-dom/client";
import "./index.css";
// Avant App : les traductions doivent être prêtes au premier rendu.
import "./i18n";
import App from "./App";
import {Provider} from "react-redux";
import {store} from "./store/store";
import {createBrowserRouter} from "react-router";
// `RouterProvider` est la seule API à venir de `react-router/dom` : react-router 8 a
// supprimé le paquet `react-router-dom` et réparti ses exports entre `react-router` et
// `react-router/dom`, ce dernier ne portant que ce qui dépend du DOM.
import {RouterProvider} from "react-router/dom";
import PrivateRoutes from "./PrivateRoutes";
import Layout from "./Layout";
import {ComponentType} from "react";
import RouteErrorOverlay from "./component/RouteErrorOverlay";
import {listenToChunkLoadErrors} from "./service/ChunkLoadError";

// Chaque écran est chargé à la demande, dans son propre chunk : sans ça, toute visite chargeait et
// exécutait le bundle entier (6,8 Mo en mode development, 9,5 Mo instrumenté pour Cypress), y
// compris le code des écrans qu'elle n'affiche pas. Mesuré en local le 24/09/2026 : lecture et
// exécution du bundle comptaient pour ~0,6 s de chaque visite, soit la moitié du chargement d'une
// page légère. App, Layout et PrivateRoutes restent dans le bundle principal : ils enveloppent
// toutes les pages.
const asRoute = (module: {default: ComponentType}) => ({Component: module.default});

// Un chunk introuvable (déploiement survenu pendant que l'onglet était ouvert) aboutit sur
// `RouteErrorOverlay`, qui recharge la page : cf. `ChunkLoadError.ts`.
listenToChunkLoadErrors(window);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      {
        path: "/",
        element: <Layout />,
        errorElement: <RouteErrorOverlay />,
        children: [
          {
            path: "fleet",
            lazy: () => import("./app/fleet/FleetManagement").then(asRoute),
            children: [
              {
                path: "client",
                lazy: () => import("./app/fleet/clients/ListsClientSearchView").then(asRoute),
              },
              {
                path: "plant",
                lazy: () => import("./app/fleet/plants/ListsPlantSearchView").then(asRoute),
              },
              {
                element: <PrivateRoutes authField="authAppInstallationControl" />,
                children: [
                  {
                    path: "control",
                    lazy: () =>
                      import("./app/fleet/controls/installation-control/InstallationControlView").then(asRoute),
                  },
                  {
                    path: "plants-to-control",
                    lazy: () => import("./app/fleet/controls/plants-to-control/PlantsToControlView").then(asRoute),
                  },
                ],
              },
            ],
          },
          {
            path: "platform",
            lazy: () => import("./app/platform/Platform").then(asRoute),
            children: [
              {
                element: <PrivateRoutes authField="authAppUserAdmin" />,
                children: [
                  {
                    path: "user",
                    lazy: () => import("./app/platform/users/ListsUserSearchView").then(asRoute),
                  },
                ],
              },
              {
                element: <PrivateRoutes authField="authAppParkAdmin" />,
                children: [
                  {
                    path: "park",
                    lazy: () => import("./app/platform/agencies/ListsAgencySearchView").then(asRoute),
                  },
                ],
              },
              {
                path: "notification",
                lazy: () => import("./app/platform/notifications/SendNotificationView").then(asRoute),
              },
            ],
          },
          {
            path: "sizea",
            lazy: () => import("./app/pre-sale/sizea/Sizea").then(asRoute),
            children: [
              {
                path: "form",
                lazy: () => import("./app/pre-sale/sizea/SizeaForm").then(asRoute),
              },
              {
                path: "simulation",
                lazy: () => import("./app/pre-sale/sizea/SimulationList").then(asRoute),
              },
              {
                path: "simulations",
                lazy: () => import("./app/pre-sale/sizea/Simulations").then(asRoute),
              },
            ],
          },
          {
            path: "economical-study",
            lazy: () => import("./app/pre-sale/economicalStudy/EconomicalStudy").then(asRoute),
            children: [
              {
                path: "form",
                lazy: () => import("./app/pre-sale/economicalStudy/EconomicalStudyForm").then(asRoute),
              },
              {
                path: "study",
                lazy: () => import("./app/pre-sale/economicalStudy/EconomicalStudyDownload").then(asRoute),
              },
            ],
          },
          {
            path: "implantation",
            lazy: () => import("./app/pre-sale/implantation/Implantation").then(asRoute),
            children: [
              {
                path: "",
                lazy: () => import("./app/pre-sale/implantation/ImplantationView").then(asRoute),
              },
            ],
          },
          {
            path: "studies",
            lazy: () => import("./app/pre-sale/studies/Studies").then(asRoute),
            children: [
              {
                path: "list",
                lazy: () => import("./app/pre-sale/studies/StudyListView").then(asRoute),
                children: [
                  {
                    path: ":folderId?",
                    lazy: () => import("./app/pre-sale/studies/components/FolderDetail").then(asRoute),
                  },
                ],
              },
            ],
          },
          {
            path: "scada",
            lazy: () => import("./app/scada/Scada").then(asRoute),
            children: [
              {
                path: "plants/*",
                lazy: () => import("./app/scada/fleet-view/PlantsDataView").then(asRoute),
              },
              {
                path: "plant/*",
                lazy: () => import("./app/scada/plant-view/PlantDataView").then(asRoute),
              },
              {
                path: "alarms/*",
                lazy: () => import("./app/scada/alarms/AlarmsNotViewedBySavView").then(asRoute),
              },
              {
                path: "restart_by_alarm",
                lazy: () => import("./app/scada/remote/ScadaRestartByAlarmView").then(asRoute),
              },
            ],
          },
          {
            path: "resource",
            lazy: () => import("./app/resource/Resource").then(asRoute),
            children: [
              {
                element: <PrivateRoutes authField="authAppSoftware" />,
                children: [
                  {
                    path: "software",
                    lazy: () => import("./app/resource/software/SoftwareView").then(asRoute),
                  },
                ],
              },
              {
                element: <PrivateRoutes authField="authAppDocumentation" />,
                children: [
                  {
                    path: "documentations",
                    lazy: () => import("./app/resource/documentation/DocumentationView").then(asRoute),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

root.render(
  <Provider store={store}>
    <RouterProvider router={router} />
  </Provider>,
);
